import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Comprehensive Completed Plans Data Synchronization Utility
 * This script analyzes all data sources and creates the proper completed plans structure
 */
export const testCompletedPlansBackfill = async () => {
  console.log('🚀 Starting Comprehensive Completed Plans Data Sync...');
  
  try {
    // Step 1: Get all families
    console.log('📊 Step 1: Fetching all families...');
    const familiesSnapshot = await getDocs(collection(db, 'families'));
    const families = familiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log(`Found ${families.length} families`);

    // Step 2: Get all users
    console.log('👥 Step 2: Fetching all users...');
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log(`Found ${users.length} users`);

    // Step 3: Get all plans
    console.log('📖 Step 3: Fetching all plans...');
    const plansSnapshot = await getDocs(collection(db, 'plans'));
    const plans = plansSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log(`Found ${plans.length} plans`);

    // Step 4: Get all recordings
    console.log('🎤 Step 4: Fetching all recordings...');
    const recordingsSnapshot = await getDocs(collection(db, 'recordings'));
    const recordings = recordingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log(`Found ${recordings.length} recordings`);

    // Step 5: Get all user listening plans
    console.log('👂 Step 5: Fetching user listening plans...');
    const allListeningPlans = [];
    for (const user of users) {
      const listeningPlansSnapshot = await getDocs(
        collection(db, `users/${user.id}/listeningPlans`)
      );
      const userListeningPlans = listeningPlansSnapshot.docs.map(doc => ({
        id: doc.id,
        userId: user.id,
        ...doc.data()
      }));
      allListeningPlans.push(...userListeningPlans);
    }
    console.log(`Found ${allListeningPlans.length} listening plan subscriptions`);

    // Step 6: Process each family
    console.log('🔍 Step 6: Processing each family...');
    
    for (const family of families) {
      console.log(`\n📋 Processing family: ${family.name} (${family.id})`);
      
      // Find family members using multiple methods
      let familyMembers = users.filter(user => 
        user.familyIds && user.familyIds.includes(family.id)
      );
      
      if (familyMembers.length === 0) {
        familyMembers = users.filter(user => 
          user.familyId === family.id || 
          (user.families && user.families.includes(family.id)) ||
          (user.family && user.family === family.id)
        );
      }
      
      if (familyMembers.length === 0) {
        const familyName = family.name || family.familyName;
        if (familyName) {
          familyMembers = users.filter(user => 
            user.family === familyName || 
            user.familyName === familyName ||
            (user.families && user.families.some(f => f.name === familyName))
          );
        }
      }
      
      console.log(`  👥 Family members found: ${familyMembers.length}`);
      
      // Get family's completed chapters
      const completedChaptersSnapshot = await getDocs(
        collection(db, `families/${family.id}/completedChapters`)
      );
      const familyCompletedChapters = completedChaptersSnapshot.docs.map(doc => ({
        id: doc.id,
        familyId: family.id,
        ...doc.data()
      }));
      console.log(`  📚 Existing completed chapters: ${familyCompletedChapters.length}`);
      
      // Analyze recordings to determine completed chapters
      const familyRecordings = recordings.filter(recording => {
        const recordingUser = users.find(u => u.id === recording.userId);
        return recordingUser && familyMembers.some(member => member.id === recordingUser.id);
      });
      console.log(`  🎤 Family recordings: ${familyRecordings.length}`);
      
      // Group recordings by book and chapter
      const recordingsByChapter = {};
      familyRecordings.forEach(recording => {
        const chapterKey = `${recording.book}_${recording.chapter}`;
        if (!recordingsByChapter[chapterKey]) {
          recordingsByChapter[chapterKey] = {
            book: recording.book,
            chapter: recording.chapter,
            recordings: []
          };
        }
        recordingsByChapter[chapterKey].recordings.push(recording);
      });
      
      console.log(`  📊 Unique chapters with recordings: ${Object.keys(recordingsByChapter).length}`);
      
      // Update family completed chapters if needed
      const completedChapterKeys = Object.keys(recordingsByChapter);
      if (completedChapterKeys.length > 0) {
        // Check if we need to update the completed chapters
        const existingAllChapters = familyCompletedChapters.find(cc => cc.id === 'all-chapters');
        const needsUpdate = !existingAllChapters || 
          existingAllChapters.completedChapters !== completedChapterKeys.length ||
          !existingAllChapters.chapters ||
          existingAllChapters.chapters.length !== completedChapterKeys.length;
        
        if (needsUpdate) {
          console.log(`  🔄 Updating family completed chapters...`);
          
          const completedChaptersData = {
            chapters: Object.values(recordingsByChapter).map(chapter => ({
              book: chapter.book,
              chapter: chapter.chapter
            })),
            completedChapters: completedChapterKeys.length,
            lastUpdated: serverTimestamp()
          };
          
          if (existingAllChapters) {
            await updateDoc(existingAllChapters.ref, completedChaptersData);
          } else {
            await addDoc(collection(db, `families/${family.id}/completedChapters`), {
              id: 'all-chapters',
              ...completedChaptersData
            });
          }
          
          console.log(`  ✅ Updated completed chapters: ${completedChapterKeys.length} chapters`);
        }
      }
      
      // Process each plan to determine completion status
      console.log(`  📖 Processing ${plans.length} plans...`);
      
      for (const plan of plans) {
        console.log(`    📋 Analyzing plan: ${plan.name}`);
        
        const planChapters = plan.chapters || [];
        const planCompletedChapters = [];
        
        // Check which chapters of this plan are completed
        for (const planChapter of planChapters) {
          const chapterKey = `${planChapter.book}_${planChapter.chapter}`;
          if (recordingsByChapter[chapterKey]) {
            planCompletedChapters.push(chapterKey);
          }
        }
        
        const isPlanCompleted = planCompletedChapters.length >= planChapters.length && planChapters.length > 0;
        console.log(`      📊 Progress: ${planCompletedChapters.length}/${planChapters.length} chapters`);
        console.log(`      ✅ Completed: ${isPlanCompleted}`);
        
        if (isPlanCompleted) {
          // Get family members who are subscribed to this plan for listening
          const familyListeningPlans = allListeningPlans.filter(
            lp => lp.userId && familyMembers.some(member => member.id === lp.userId) && lp.planId === plan.id
          );
          
          // Get family members who are currently listening
          const currentlyListening = familyListeningPlans
            .filter(lp => lp.isCurrentlyListening)
            .map(lp => lp.userId);
          
          const formerlyListening = familyListeningPlans
            .filter(lp => !lp.isCurrentlyListening)
            .map(lp => lp.userId);
          
          // Create or update completed plan document
          const completedPlanData = {
            planId: plan.id,
            planName: plan.name,
            planDescription: plan.description,
            totalChapters: planChapters.length,
            completedChapters: planCompletedChapters,
            status: 'completed',
            completedAt: serverTimestamp(),
            archivedAt: serverTimestamp(),
            currentlySubscribed: familyListeningPlans.map(lp => lp.userId),
            formerlySubscribed: [], // Will be populated as users unsubscribe
            currentlyListening: currentlyListening,
            formerlyListening: formerlyListening
          };
          
          // Check if completed plan already exists
          const existingCompletedPlansSnapshot = await getDocs(
            collection(db, `families/${family.id}/completedPlans`)
          );
          const existingPlan = existingCompletedPlansSnapshot.docs.find(
            doc => doc.data().planId === plan.id
          );
          
          if (existingPlan) {
            console.log(`      🔄 Updating existing completed plan...`);
            await updateDoc(existingPlan.ref, completedPlanData);
          } else {
            console.log(`      ➕ Creating new completed plan...`);
            await addDoc(collection(db, `families/${family.id}/completedPlans`), completedPlanData);
          }
          
          console.log(`      ✅ Completed plan processed for family ${family.name}`);
          console.log(`        📊 Subscribed: ${familyListeningPlans.length} users`);
          console.log(`        🎧 Currently listening: ${currentlyListening.length} users`);
          console.log(`        📚 Completed chapters: ${planCompletedChapters.length}`);
        }
      }
    }

    // Step 7: Summary
    console.log('\n🎉 Data Sync Complete! Summary:');
    console.log(`📊 Families processed: ${families.length}`);
    console.log(`👥 Users analyzed: ${users.length}`);
    console.log(`📖 Plans analyzed: ${plans.length}`);
    console.log(`🎤 Recordings analyzed: ${recordings.length}`);
    console.log(`👂 Listening subscriptions: ${allListeningPlans.length}`);
    
    // Count total completed plans created
    let totalCompletedPlans = 0;
    for (const family of families) {
      const completedPlansSnapshot = await getDocs(
        collection(db, `families/${family.id}/completedPlans`)
      );
      totalCompletedPlans += completedPlansSnapshot.docs.length;
    }
    console.log(`✅ Total completed plans created: ${totalCompletedPlans}`);
    
    console.log('\n🚀 Sync completed! Check your Dashboard and Profile pages to see the results.');
    
  } catch (error) {
    console.error('❌ Error during data sync:', error);
    throw error;
  }
};

/**
 * Clean up test data (optional)
 */
export const cleanupTestData = async () => {
  console.log('🧹 Cleaning up test data...');
  
  try {
    const familiesSnapshot = await getDocs(collection(db, 'families'));
    
    for (const familyDoc of familiesSnapshot.docs) {
      const completedPlansSnapshot = await getDocs(
        collection(db, `families/${familyDoc.id}/completedPlans`)
      );
      
      for (const completedPlanDoc of completedPlansSnapshot.docs) {
        await completedPlanDoc.ref.delete();
      }
    }
    
    console.log('✅ Test data cleaned up');
  } catch (error) {
    console.error('❌ Error cleaning up test data:', error);
    throw error;
  }
};